import { apiClient } from '../client';
import { API_ENDPOINTS } from '../config';

export interface DashboardMetric {
  value: number;
  previousValue: number | null;
  changePct: number | null;
  basis: string;
}

export interface DashboardRevenueMetric extends DashboardMetric {
  bySource?: Record<string, { amount: number; orders: number }>;
  skippedNonInr?: number;
}

export interface DashboardOrdersMetric extends DashboardMetric {
  bySource?: Record<string, { orders: number }>;
}

export interface DashboardStats {
  conversations: number;
  contacts: number;
  messages: number;
  campaigns: number;
  responseRate: number;
  abandonedCheckouts: number;
  aiCredits?: {
    total: number;
    used: number;
    available: number;
    plan?: string | null;
  } | null;
  metrics?: {
    range?: { start: string; end: string };
    previousRange?: { start: string; end: string };
    revenue?: DashboardRevenueMetric;
    orders?: DashboardOrdersMetric;
    conversations?: DashboardMetric;
    newLeads?: DashboardMetric;
    messages?: DashboardMetric;
    campaigns?: DashboardMetric;
  } | null;
  aiInsight?: {
    analysisDate: string | null;
    status: string | null;
    summary: string | null;
    aiInsights: string[];
    metrics: {
      total_conversations?: number | null;
      analyzed_conversations?: number | null;
      success_conversations?: number | null;
      failure_conversations?: number | null;
      neutral_conversations?: number | null;
      success_rate_percent?: number | null;
      failure_rate_percent?: number | null;
      neutral_rate_percent?: number | null;
      token_usage?: {
        input_tokens?: number;
        output_tokens?: number;
        total_tokens?: number;
        total_cost_usd?: number;
        cost_breakdown?: {
          input_cost_usd?: number;
          output_cost_usd?: number;
        };
      } | null;
      [key: string]: any;
    } | null;
    error: string | null;
    triggeredAt: string | null;
    updatedAt: string | null;
  } | null;
}

export interface RecentActivity {
  id: string;
  type: 'message' | 'campaign' | 'contact';
  title: string;
  description: string;
  timestamp: string;
  status?: string;
  contact?: {
    name: string;
    phone_number: string;
  };
}

export interface AgentDashboardStats {
  stats: {
    contactsAssigned: number;
    totalConversations: number;
    messagesSent: number;
  };
  weeklyActivity: {
    day: string;
    messages: number;
    conversations: number;
  }[];
  monthlyContactsGrowth: {
    month: string;
    contacts: number;
  }[];
}

export const dashboardApi = {
  async getStats(
    workspaceScopeId: string | null,
    organizationId?: string | null,
    range?: { startDate?: string; endDate?: string; include?: 'metrics'; tzOffsetMinutes?: number }
  ): Promise<{ data: DashboardStats; error: string | null; statusCode?: number }> {
    try {
      if (!workspaceScopeId && !organizationId) {
        return {
          data: {
            conversations: 0,
            contacts: 0,
            messages: 0,
            campaigns: 0,
            responseRate: 0,
            abandonedCheckouts: 0,
            aiCredits: null,
            aiInsight: null,
          },
          error: 'No business account or organization selected',
        };
      }

      const optimized = await apiClient.get<DashboardStats>(API_ENDPOINTS.DASHBOARD_STATS, {
        params: {
          ...(workspaceScopeId ? { workspaceId: workspaceScopeId } : {}),
          ...(organizationId ? { organization_id: organizationId } : {}),
          ...(range?.startDate ? { startDate: range.startDate } : {}),
          ...(range?.endDate ? { endDate: range.endDate } : {}),
          ...(range?.include ? { include: range.include } : {}),
          ...(range?.tzOffsetMinutes != null ? { tzOffsetMinutes: range.tzOffsetMinutes } : {}),
        },
      });

      if (optimized && !optimized.error && optimized.data) {
        return { data: optimized.data as any, error: null };
      }

      return {
        data: {
          conversations: 0,
          contacts: 0,
          messages: 0,
          campaigns: 0,
          responseRate: 0,
          abandonedCheckouts: 0,
          aiCredits: null,
          aiInsight: null,
        },
        error: optimized?.error || 'Failed to load dashboard stats',
        // Lets the caller skip retrying a 504 (see the query retry rule in App.tsx).
        statusCode: (optimized as { statusCode?: number } | null)?.statusCode,
      };
    } catch (error) {
      return {
        data: {
          conversations: 0,
          contacts: 0,
          messages: 0,
          campaigns: 0,
          responseRate: 0,
          abandonedCheckouts: 0,
          aiCredits: null,
          aiInsight: null,
        },
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Just the `aiCredits` block of getStats, from /dashboard/ai-credits — same
   * scope params, none of the dashboard counts behind it.
   */
  async getAiCredits(
    workspaceScopeId: string | null,
    organizationId?: string | null
  ): Promise<{ data: Pick<DashboardStats, 'aiCredits'>; error: string | null }> {
    if (!workspaceScopeId && !organizationId) {
      return { data: { aiCredits: null }, error: 'No business account or organization selected' };
    }
    try {
      const res = await apiClient.get<Pick<DashboardStats, 'aiCredits'>>(API_ENDPOINTS.DASHBOARD_AI_CREDITS, {
        params: {
          ...(workspaceScopeId ? { workspaceId: workspaceScopeId } : {}),
          ...(organizationId ? { organization_id: organizationId } : {}),
        },
      });
      if (res && !res.error && res.data) {
        return { data: res.data, error: null };
      }
      return { data: { aiCredits: null }, error: res?.error || 'Failed to load AI credits' };
    } catch (error) {
      return {
        data: { aiCredits: null },
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  async getRecentActivity(businessAccountId: string | null): Promise<{ data: RecentActivity[]; error: string | null }> {
    try {
      if (!businessAccountId) {
        return {
          data: [],
          error: 'No business account selected',
        };
      }

      const [conversationsRes, campaignsRes] = await Promise.all([
        apiClient.get(API_ENDPOINTS.CONVERSATIONS, {
          params: {
            businessAccountId: businessAccountId,
            orderBy: 'updated_at',
            ascending: false,
            limit: 5,
          },
        }),
        apiClient.get(API_ENDPOINTS.CAMPAIGNS, {
          params: {
            businessAccountId: businessAccountId,
            orderBy: 'updated_at',
            ascending: false,
            limit: 3,
          },
        }),
      ]);

      const conversations = conversationsRes.data || [];
      const campaigns = campaignsRes.data || [];

      const activity: RecentActivity[] = [];

      for (const conv of conversations) {
        const contact = conv.contact;

        const contactName = contact?.name || conv.phoneNumber || 'Unknown';

        const conversationTimestamp =
          conv.lastMessageAt ||
          conv.last_message_at ||
          conv.lastMessage?.createdAt ||
          conv.last_message?.created_at ||
          conv.updatedAt ||
          conv.updated_at ||
          conv.createdAt ||
          conv.created_at ||
          new Date().toISOString();

        let description = `Conversation with ${contactName}`;
        let title = 'Conversation Activity';

        if (conv.last_message) {
          if (conv.last_message.is_inbound) {
            title = 'New Message';
            description = `Message from ${contactName}`;
          } else {
            title = 'Message Sent';
            description = `Message to ${contactName}`;
          }
        } else {
          title = 'New Conversation';
          description = `Started with ${contactName}`;
        }

        activity.push({
          id: `conv-${conv.id}`,
          type: 'message',
          title: title,
          description: description,
          timestamp: conversationTimestamp,
          status: conv.status,
          contact: {
            name: contactName,
            phone_number: contact?.phoneNumber || conv.phoneNumber || 'N/A',
          },
        });
      }

      campaigns.forEach((campaign) => {

        const timestamp = campaign.updated_at || campaign.completed_at || campaign.created_at;

        const finalTimestamp = timestamp || new Date().toISOString();

        activity.push({
          id: `campaign-${campaign.id}`,
          type: 'campaign',
          title: 'Campaign updated',
          description: `${campaign.name} - ${campaign.status}`,
          timestamp: finalTimestamp,
          status: campaign.status,
        });
      });

      activity.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

      return {
        data: activity,
        error: null,
      };
    } catch (error) {
      return {
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },
};

export const agentDashboardApi = {
  async getAgentDashboardStats(businessAccountId: string | null): Promise<{ data: AgentDashboardStats | null; error: string | null }> {
    try {
      const response = await apiClient.get<AgentDashboardStats>(API_ENDPOINTS.AGENT_DASHBOARD, {
        params: { businessAccountId },
      });

      if (response && response.data) {
        return { data: response.data, error: null };
      }

      return { data: null, error: 'No data returned' };
    } catch (error) {
      return {
        data: null,
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },
};
